import { useLayoutEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * La `rightItemsBox` du `Header` de `@backstage/core-components` : une boîte
 * flex centrée verticalement, déjà occupée par l'étoile favori et le menu ⋮
 * sur les pages d'entité.
 *
 * Elle est ciblée par sa STRUCTURE, jamais par son nom de classe JSS. En
 * production, Material-UI v4 remplace `BackstageHeader-rightItemsBox-<n>` par
 * `jss4-<n>` : le nom du composant et celui de la règle disparaissent
 * (`createGenerateClassName`, `productionPrefix` fixé par
 * `UnifiedThemeProvider`). Un sélecteur bâti dessus marche en dev et ne matche
 * jamais sur le cluster — c'est exactement ce qui laissait le bandeau vide.
 *
 * `MuiGrid-container`, lui, survit au build : Material-UI conserve les noms
 * lisibles de ses propres composants (branche `name.indexOf('Mui') === 0`).
 * C'est la seule raison pour laquelle les sélecteurs `[class*="Mui…"]` de
 * `theme.ts` fonctionnent — la technique ne se transpose pas aux classes
 * `Backstage*`.
 *
 * `Header` rend `<header>` avec exactement deux enfants : le `leftItemsBox`
 * (un `Box`) puis le `rightItemsBox` (un `Grid container`). L'app ne rend
 * aucun `AppBar`, donc `<header>` désigne sans ambiguïté ce bandeau.
 */
const HEADER_RIGHT_SELECTOR = 'header > [class*="MuiGrid-container"]';

/**
 * Point d'ancrage de la page d'entité NATIVE du catalogue (phase 4a).
 *
 * Celle-ci ne rend aucun `<header>` : ses actions — l'étoile favori, le menu ⋮ —
 * vivent dans un `div.bui-HeaderControls` du système de composants Backstage
 * (`bui`). Le sélecteur historique ne trouvait donc plus rien sur ces pages, et
 * le sélecteur d'environnement disparaissait en silence : `useHeaderRightSlot`
 * renvoie `null`, et les appelants affichent leur repli — ou rien.
 *
 * Même prudence que pour le sélecteur historique : on cible `bui-HeaderControls`,
 * le nom GLOBAL et stable, et non `Header_bui-HeaderControls__<hash>` qui
 * l'accompagne et qui est un nom de module CSS, donc susceptible de changer à
 * chaque build.
 */
const NATIVE_HEADER_RIGHT_SELECTOR = '[class~="bui-HeaderControls"]';

/**
 * Renvoie le point d'ancrage pour projeter du contenu dans l'en-tête de la
 * page courante, ou `null` s'il n'y en a pas.
 *
 * `EntityLayout` n'expose aucune prop d'injection (`EntityLayoutProps` se
 * limite à `UNSTABLE_extraContextMenuItems`, `UNSTABLE_contextMenuOptions`,
 * `children`, `NotFoundComponent`, `parentEntityRelations`) : un portail est
 * la seule voie qui n'implique pas de réimplémenter le composant.
 *
 * Les appelants DOIVENT gérer le cas `null` par un repli visible. Toutes les
 * pages de tête de cette app rendent un `Header`, mais rien ne le garantit
 * pour chaque route — et ce qu'on y projette (déconnexion, notifications)
 * ne doit jamais devenir inaccessible.
 */
export function useHeaderRightSlot(): HTMLElement | null {
  const { pathname } = useLocation();
  const [slot, setSlot] = useState<HTMLElement | null>(null);

  // `useLayoutEffect`, pas `useEffect` : ce dernier s'exécute après la
  // peinture, donc le premier rendu peindrait toujours le repli (flash +
  // saut de mise en page) avant de basculer dans le portail. Synchrone et
  // avant peinture, `useLayoutEffect` évite ce frame visible à chaque
  // chargement de page.
  useLayoutEffect(() => {
    // L'application mêle les deux systèmes le temps de la migration : pages
    // converties avec leur `Header` legacy, page d'entité native avec le sien.
    // On cherche l'un puis l'autre plutôt que de supposer lequel est monté.
    const find = () =>
      document.querySelector<HTMLElement>(HEADER_RIGHT_SELECTOR) ??
      document.querySelector<HTMLElement>(NATIVE_HEADER_RIGHT_SELECTOR);

    let current = find();
    setSlot(current);

    // Le point d'ancrage peut apparaître plus tard dans le DOM (résolution
    // asynchrone de la page qui le rend) : on attend son apparition plutôt
    // que de conclure trop tôt à son absence. Il peut aussi être remplacé
    // sans changement de chemin — la page de connexion laisse place à la
    // page d'accueil sur `/` — : un ancrage retiré du DOM est abandonné pour
    // le suivant, sinon le contenu projeté y resterait, invisible.
    const observer = new MutationObserver(() => {
      if (current?.isConnected) return;
      current = find();
      setSlot(current);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, [pathname]);

  return slot;
}
